// 1. Make sure to import ReactDOM at the top of your file
import ReactDOM from "react-dom";
import React, { useState } from "react";
import {
  FaChartPie,
  FaUsers,
  FaChalkboardTeacher,
  FaBookOpen,
  FaUserFriends,
  FaChartBar,
  FaCreditCard,
  FaLock,
  FaTasks,
  FaChevronUp,
  FaChevronDown,
} from "react-icons/fa";
import {
  FolderPlus,
  Briefcase,
  GraduationCap,
  BadgeCheck,
  Building,
  Layout,
  LayoutDashboard,
} from "lucide-react";
import kaas from "../Images/Vetrina _Logo.png";
import { MdAssignment } from "react-icons/md";

// ===================================================================
// 2. The self-contained Tooltip component using a React Portal
// It renders the tooltip directly onto the document body, bypassing any overflow issues.
// ===================================================================
const HoverTooltip = ({ text, top, left }) => {
  if (!text) {
    return null;
  }

  const style = {
    position: 'fixed',
    top: `${top}px`,
    left: `${left}px`,
    transform: 'translateY(-50%)',
    backgroundColor: '#000000e0',
    color: '#fff',
    padding: '6px 12px',
    borderRadius: '6px',
    whiteSpace: 'nowrap',
    zIndex: 9999, // Ensure it's on top of everything
    fontSize: '0.9rem',
    fontWeight: '500',
    boxShadow: '0px 4px 12px rgba(0,0,0,0.2)',
    pointerEvents: 'none',
  };

  return ReactDOM.createPortal(<div style={style}>{text}</div>, document.body);
};


// ===================================================================
// 3. The Updated Sidebar Component
// ===================================================================
const Sidebar = ({ activeMenu, setActiveMenu, isOpen }) => {
  const [expandedMenu, setExpandedMenu] = useState(null);
  // NEW STATE: Manages the tooltip's visibility, text, and position
  const [tooltip, setTooltip] = useState({ visible: false, text: '', top: 0, left: 0 });


  const menuItems = [
    { id: "dashboard", label: "Dashboard", icon: <FaChartPie /> },
    { id: "tmsdashboard", label: "TMS Dashboard", icon: <LayoutDashboard /> },
    { id: "user-management", label: "User Management", icon: <FaUsers /> },
    {
      id: "admin-master",
      label: "Master",
      icon: <FaTasks />,
      subItems: [
        { id: "create-category", label: "Create Category", icon: <FolderPlus size={18} /> },
        { id: "create-occupation", label: "Create Occupation", icon: <Briefcase size={18} /> },
        { id: "create-education", label: "Create Education", icon: <GraduationCap size={18} /> },
        { id: "create-department", label: "Create Department", icon: <FaUserFriends size={18} /> },
        { id: "create-designation", label: "Create Designation", icon: <BadgeCheck size={18} /> },
        { id: "create-division", label: "Create Division", icon: <Building size={18} /> },
        { id: "create-subdivision", label: "Create Subdivision", icon: <Layout size={18} /> },
      ],
    },
    { id: "course-assignment", label: "Course Assign", icon: <MdAssignment /> },
    { id: "instructor-management", label: "Instructor Management", icon: <FaChalkboardTeacher /> },
    { id: "course-approval", label: "Course Creation", icon: <FaBookOpen /> },
    { id: "group-management", label: "Group Management", icon: <FaUserFriends /> },
    { id: "reports-analytics", label: "Reports & Analytics", icon: <FaChartBar /> },
    { id: "billing-payments", label: "Payment Requests", icon: <FaCreditCard /> },
    { id: "create-membership", label: "Create Membership", icon: <FaLock /> },
  ];

  const toggleSubMenu = (id) => {
    setExpandedMenu((prev) => (prev === id ? null : id));
  };

  // NEW HANDLERS: Calculate position and set state to show/hide the tooltip portal
  const handleMouseEnter = (e, text) => {
    if (!isOpen) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        visible: true,
        text: text,
        top: rect.top + rect.height / 2, // Center vertically
        left: rect.right + 10,           // Position to the right with a 10px gap
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip({ visible: false, text: '', top: 0, left: 0 });
  };


  return (
    <>
      {/* 4. Render the portal component. It's only visible when the state is active. */}
      <HoverTooltip
        text={tooltip.visible ? tooltip.text : ''}
        top={tooltip.top}
        left={tooltip.left}
      />

      <div
        style={{
          width: isOpen ? "260px" : "70px",
          background: "#f4811ee5",
          color: "white",
          transition: "all 0.3s ease",
          overflowX: "hidden", // This is the property that makes the portal necessary
          display: "flex",
          flexDirection: "column",
          zIndex: 100,
          position: "relative",
          height: "100vh",
        }}
      >
        {/* Scrollbar styles */}
        <style>{`
          .sidebar-scroll::-webkit-scrollbar { width: 4px; }
          .sidebar-scroll::-webkit-scrollbar-track { background: transparent; }
          .sidebar-scroll::-webkit-scrollbar-thumb { background-color: rgba(255, 255, 255, 0.3); border-radius: 10px; }
          .sidebar-scroll::-webkit-scrollbar-thumb:hover { background-color: rgba(255, 255, 255, 0.5); }
        `}</style>

        {/* Sticky logo header */}
        <div
          style={{
            padding: "15px",
            borderBottom: "1px solid rgba(255,255,255,0.2)",
            backgroundColor: "white",
            position: "sticky",
            top: 0,
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            justifyContent: isOpen ? "flex-start" : "center",
            height: "65px",
          }}
        >
          {isOpen ? (
            <img
              src={kaas}
              alt="Admin Panel"
              style={{
                width: "140px",
                height: "auto",
                transition: "all 0.3s ease",
              }}
            />
          ) : (
            <div style={{ fontSize: "14px", fontWeight: "bold", color: "#000" }}>
              Admin
            </div>
          )}
        </div>

        {/* Sidebar nav */}
        <nav
          className="sidebar-scroll"
          style={{ flex: 1, overflowY: "auto", padding: "15px 10px" }}
        >
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {menuItems.map((item) => (
              <React.Fragment key={item.id}>
                <li
                  // Removed the native `title` attribute
                  // 5. Added mouse event handlers to trigger the tooltip
                  onMouseEnter={(e) => handleMouseEnter(e, item.label)}
                  onMouseLeave={handleMouseLeave}
                  onClick={() => {
                    if (item.subItems) {
                      toggleSubMenu(item.id);
                    } else {
                      setActiveMenu(item.id);
                    }
                  }}
                  style={{
                    padding: isOpen ? "10px 20px" : "10px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    backgroundColor:
                      activeMenu === item.id ? "#ffffff" : "transparent",
                    borderLeft:
                      activeMenu === item.id
                        ? "2px solid #f4811e"
                        : "2px solid transparent",
                    marginBottom: "2px",
                    color: activeMenu === item.id ? "#f4811e" : "#ffffff",
                    fontWeight: activeMenu === item.id ? "600" : "normal",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div
                    style={{
                      fontSize: "18px",
                      marginRight: isOpen ? "10px" : "0",
                      textAlign: isOpen ? "left" : "center",
                      width: isOpen ? "auto" : "100%",
                      color: activeMenu === item.id ? "#f4811e" : "#ffffff",
                    }}
                  >
                    {item.icon}
                  </div>
                  <span
                    style={{
                      display: isOpen ? "block" : "none",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: activeMenu === item.id ? "#f4811e" : "#ffffff",
                    }}
                  >
                    {item.label}
                  </span>
                  {isOpen && item.subItems && (
                    <span
                      style={{
                        marginLeft: "auto",
                        color: activeMenu === item.id ? "#f4811e" : "#ffffff",
                      }}
                    >
                      {expandedMenu === item.id ? (
                        <FaChevronUp size={12} />
                      ) : (
                        <FaChevronDown size={12} />
                      )}
                    </span>
                  )}
                </li>

                {/* Sub Items */}
                {item.subItems &&
                  expandedMenu === item.id &&
                  item.subItems.map((sub) => (
                    <li
                      key={sub.id}
                      // Added handlers to sub-items as well
                      onMouseEnter={(e) => handleMouseEnter(e, sub.label)}
                      onMouseLeave={handleMouseLeave}
                      onClick={() => setActiveMenu(sub.id)}
                      style={{
                        padding: isOpen ? "10px 40px" : "10px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        backgroundColor:
                          activeMenu === sub.id
                            ? "rgba(255,255,255,0.15)"
                            : "transparent",
                        borderLeft:
                          activeMenu === sub.id
                            ? "4px solid white"
                            : "4px solid transparent",
                        fontSize: "14px",
                        marginBottom: "4px",
                        color: "#fff",
                      }}
                    >
                      <div
                        style={{
                          marginRight: isOpen ? "8px" : "0",
                          fontSize: "16px",
                          textAlign: isOpen ? "left" : "center",
                          width: isOpen ? "auto" : "100%",
                        }}
                      >
                        {sub.icon}
                      </div>
                      <span
                        style={{
                          display: isOpen ? "block" : "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {sub.label}
                      </span>
                    </li>
                  ))}
              </React.Fragment>
            ))}
          </ul>
        </nav>

        {/* Footer */}
        <div
          style={{
            padding: "10px",
            borderTop: "1px solid rgba(255,255,255,0.2)",
            textAlign: "center",
            fontSize: isOpen ? "12px" : "0",
            transition: "all 0.3s ease",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          © 2025 Admin Dashboard
        </div>
      </div>
    </>
  );
};

export default Sidebar;